import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Button, Stack, Typography } from '@mui/material';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import {
	MapContainer,
	Marker,
	TileLayer,
	useMap,
	useMapEvents,
} from 'react-leaflet';
import L from 'leaflet';
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';
import 'leaflet/dist/leaflet.css';
import { getBrowserLocation } from '../Shared/geolocation';

// CRA/webpack breaks Leaflet's default icon URLs — re-point them.
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
	iconRetinaUrl: markerIcon2x,
	iconUrl: markerIcon,
	shadowUrl: markerShadow,
});

const DEFAULT_CENTER = [39.8283, -98.5795]; // contiguous US
const DEFAULT_ZOOM = 4;
const PINNED_ZOOM = 16;

const parseCoord = (value) => {
	if (value == null || value === '') return null;
	const n = Number(value);
	return Number.isFinite(n) ? n : null;
};

/** Leaflet often mounts at 0×0 inside Collapse/modals — fix once layout settles. */
const MapInvalidateSize = () => {
	const map = useMap();
	useEffect(() => {
		const timers = [50, 200, 400].map((ms) =>
			setTimeout(() => map.invalidateSize(), ms),
		);
		return () => timers.forEach(clearTimeout);
	}, [map]);
	return null;
};

/** Fly the map only when `target` identity changes (not on every pin drag). */
const MapFlyTo = ({ target }) => {
	const map = useMap();
	const lastKeyRef = useRef(null);
	useEffect(() => {
		if (!target?.center) return;
		const key = `${target.center[0]},${target.center[1]},${target.zoom},${target.nonce}`;
		if (key === lastKeyRef.current) return;
		lastKeyRef.current = key;
		map.flyTo(target.center, target.zoom ?? map.getZoom(), { duration: 0.6 });
	}, [target, map]);
	return null;
};

const MapClickHandler = ({ disabled, onPick }) => {
	useMapEvents({
		click(event) {
			if (disabled) return;
			onPick(event.latlng.lat, event.latlng.lng);
		},
	});
	return null;
};

/**
 * Interactive map for placing a site pin by drag, click, or current location.
 */
const LocationMapPicker = ({
	latitude = '',
	longitude = '',
	onChange,
	disabled = false,
	height = 260,
}) => {
	const lat = parseCoord(latitude);
	const lng = parseCoord(longitude);
	const hasPin = lat != null && lng != null;

	const [locating, setLocating] = useState(false);
	const [geoError, setGeoError] = useState('');
	const [flyTarget, setFlyTarget] = useState(null);
	const hadPinRef = useRef(hasPin);
	const flyNonceRef = useRef(0);

	const position = useMemo(
		() => (hasPin ? [lat, lng] : null),
		[hasPin, lat, lng],
	);

	const initialCenter = position || DEFAULT_CENTER;
	const initialZoom = hasPin ? PINNED_ZOOM : DEFAULT_ZOOM;

	const requestFly = (center, zoom = PINNED_ZOOM) => {
		flyNonceRef.current += 1;
		setFlyTarget({
			center,
			zoom,
			nonce: flyNonceRef.current,
		});
	};

	// First time a pin appears (e.g. map click), zoom in once.
	useEffect(() => {
		if (hasPin && !hadPinRef.current && position) {
			requestFly(position, PINNED_ZOOM);
		}
		hadPinRef.current = hasPin;
	}, [hasPin, position]);

	const emitCoords = (nextLat, nextLng) => {
		onChange?.({
			latitude: Number(nextLat.toFixed(6)),
			longitude: Number(nextLng.toFixed(6)),
		});
	};

	const handleUseCurrentLocation = async () => {
		if (disabled) return;
		setLocating(true);
		setGeoError('');
		try {
			const coords = await getBrowserLocation();
			emitCoords(coords.latitude, coords.longitude);
			requestFly([coords.latitude, coords.longitude], PINNED_ZOOM);
		} catch (err) {
			setGeoError(
				err?.message?.includes('Location permission denied')
					? 'Location permission denied. Allow location access and try again.'
					: err?.message || 'Could not get your location. Try again.',
			);
		} finally {
			setLocating(false);
		}
	};

	return (
		<Stack spacing={1}>
			<Box
				sx={{
					height,
					width: '100%',
					borderRadius: 2,
					overflow: 'hidden',
					border: '1px solid',
					borderColor: 'divider',
					'& .leaflet-container': {
						height: '100%',
						width: '100%',
						fontFamily: 'inherit',
					},
					opacity: disabled ? 0.65 : 1,
					pointerEvents: disabled ? 'none' : 'auto',
				}}
			>
				<MapContainer
					center={initialCenter}
					zoom={initialZoom}
					scrollWheelZoom
					style={{ height: '100%', width: '100%' }}
				>
					<TileLayer
						attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
						url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
					/>
					<MapInvalidateSize />
					<MapFlyTo target={flyTarget} />
					<MapClickHandler
						disabled={disabled}
						onPick={(nextLat, nextLng) => emitCoords(nextLat, nextLng)}
					/>
					{position ? (
						<Marker
							position={position}
							draggable={!disabled}
							eventHandlers={{
								dragend: (event) => {
									const next = event.target.getLatLng();
									emitCoords(next.lat, next.lng);
								},
							}}
						/>
					) : null}
				</MapContainer>
			</Box>

			<Stack
				direction={{ xs: 'column', sm: 'row' }}
				spacing={1}
				alignItems={{ sm: 'center' }}
				justifyContent="space-between"
			>
				<Typography variant="caption" color="text.secondary">
					{hasPin
						? `${lat.toFixed(6)}, ${lng.toFixed(6)} — drag the pin or tap the map to adjust`
						: 'Tap the map to drop a pin, or use your current location'}
				</Typography>
				<Button
					size="small"
					variant="outlined"
					startIcon={<MyLocationIcon />}
					onClick={handleUseCurrentLocation}
					disabled={disabled || locating}
					sx={{ flexShrink: 0, alignSelf: { xs: 'flex-start', sm: 'auto' } }}
				>
					{locating ? 'Locating…' : 'Use current location'}
				</Button>
			</Stack>

			{geoError ? (
				<Typography variant="caption" color="error">
					{geoError}
				</Typography>
			) : null}
		</Stack>
	);
};

export default LocationMapPicker;
